import { Action } from 'cadenza/action';
import { FileExportProgressModal } from 'cadenza/file-export-progress-dialog/file-export-progress-modal';
import { logger } from 'cadenza/utils/logging';
import { alert, confirm } from 'cadenza/alert';

import i18n from './abstract-export-action.properties';

/**
 * @typedef {object} AbstractExportAction~CanExportResult
 * @property {string} [error] - If the export isn't possible due to too many rows
 * @property {string} [warning] - If the export is possible but the soft limit is reached and the
 *   user needs to be asked whether it still wants to proceed
 */

/**
 * @typedef {ActionOptions} AbstractExportAction~Options
 * @property {string} [dialogTitle] - Alternative title of the file export progress dialog
 */

export class AbstractExportAction extends Action {

  /**
   * @param {AbstractExportAction~Options} [options] - Options to configure the action and the dialog
   */
  constructor (options = {}) {
    super(options);
    this._dialogTitle = options.dialogTitle ?? i18n('dialog.title.default');
  }

  execute () {
    let dialog;
    return this._canExport()
      .then(proceedWithExport => {
        if (proceedWithExport) {
          dialog = this._createFileExportProgressDialog();
          dialog.startExportAndShowProgress();
        }
      })
      .catch((error) => {
        logger.error('Could not export table', error);
        (dialog ?? this._createFileExportProgressDialog())
          .showErrorMessage();
      });
  }

  _canExport () {
    return this.canExport()
      .then(result => {
        const { warning, error } = result;
        if (error) {
          return this._showErrorAlert(error);
        } else if (warning) {
          return confirm(
            i18n('warning.manyRows'),
            warning,
            { type: 'info', submit: i18n('common:continue') });
        }
        return true;
      });
  }

  _createFileExportProgressDialog () {
    return new FileExportProgressModal(
      this._dialogTitle,
      () => this.startExport());
  }

  _showErrorAlert (errorMessage) {
    return alert(
      i18n('error.tooManyRows'),
      errorMessage,
      { type: 'info' })
      .then(() => false);
  }

  /**
   * Checks if the export is possible.
   * Must be overridden by subclass.
   *
   * @protected
   * @return {Promise.<AbstractExportAction~CanExportResult>} A promise that resolves with
   *   warning/error information about the possibility to export
   */
  canExport () {
    return Promise.reject(new Error('Not implemented by subclass'));
  }

  /**
   * Start the actual export.
   * Must be overridden by subclass.
   *
   * @protected
   * @return {Promise.<string>} A promise that resolves with a WorkOrderId that can be used for
   *   observing the progress, fetching the result and cancellation of the progress by the user.
   */
  startExport () {
    return Promise.reject(new Error('Not implemented by subclass'));
  }

}
